import React from 'react';
import {
    IBasketRequestResource,
    IBasketResourceV10,
    filterBasket,
    IAdjustmentReadResourceV22
} from '../model/Basket';
import { ApplicationState } from 'store/store';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { isDefined } from '@pepperhq/location-sdk/dist/lib/typeInference';
import { basketApi } from '../basketApi';
import { AxiosResponse } from 'axios';
import { useAuth } from 'lib/useAuth';
import { getLocalMerchantId } from 'components/settings/localStore';
import { syncBasketFromServer } from 'store/basket/basketSlice';
import { getShouldUpdateGuestSession, resetShouldUpdateGuestSession } from 'components/user/localAuth';
import { createGuestSession } from 'store/auth/authSlice';
import {
    ExistingSharedOrderData,
    IAdjustmentLocalResource,
    IUpdateOrderAdjustment,
    OrderAdjustmentType,
    OrderErrorCodes,
    OrderScenario
} from 'components/order/model/Order';
import { useSnackbar } from 'notistack';
import { useCouponCodes } from 'components/order/coupons/useCouponCodes';
import { getSortedAdjustmentsForCheckout } from 'components/order/helpers';
import { usePlatformAwards } from 'components/order/rewards/usePlatformAwards';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';
import { UserAddress } from 'components/user/model/User';

function shallowDepsEqual(objA: any[], objB: any[]) {
    if (objA === null || objB === null || !Array.isArray(objA) || !Array.isArray(objB)) {
        return false;
    }
    if (objA.length !== objB.length) {
        return false;
    }
    for (let i = 0; i < objA.length; i++) {
        if (objA[i] !== objB[i]) {
            return false;
        }
    }
    return true;
}

const isChanged = (newState: any, prevState: any) => {
    if (!prevState) {
        return true;
    }
    return !shallowDepsEqual(newState, prevState);
};

export function useCalculateBasket(
    menuIsReady: boolean,
    scenario: OrderScenario,
    sharedData?: ExistingSharedOrderData,
    deliveryAddress?: UserAddress
) {
    const dispatch = useAppDispatch();
    const { isGuest, isLoading: isAuthLoading } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const previousState = React.useRef<any>(undefined);
    const [calculatedBasket, setCalculatedBasket] = React.useState<null | IBasketResourceV10>(null);
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
    const [loading, setLoading] = React.useState(true);
    const { menu, categories, isLoading } = useSelector((state: ApplicationState) => state.menu);
    const basket = useSelector((state: ApplicationState) => state.basket);
    const nestedModifiersEnabled = useSelector(
        (state: ApplicationState) => state.settings?.settings?.menu?.nestedModifiersEnabled ?? false
    );
    const basketItemsKey = React.useMemo(
        () => basket.items.map(i => `${i.productId}:${i.quantity}`).join(','),
        [basket.items]
    );
    const { logUserEvent } = useGAHelpers();
    const {
        coupons,
        onCouponRemove,
        onCouponAdd,
        onCouponsReset,
        couponAdjustments,
        showCoupons,
        couponsForCheckout
    } = useCouponCodes(sharedData?.coupons, calculatedBasket?.adjustments);
    const {
        value: platformAwards,
        platformAwardsEnabled,
        onAwardAdd,
        onAwardRemove,
        onMultipleAwardsEnable,
        showMoreAwardsButton,
        onAwardsReset,
        awardsForCheckout
    } = usePlatformAwards(sharedData?.selectedRewards ?? [], sharedData?.coupons);

    const getBasket = React.useCallback(
        (
            adjustments: Partial<IAdjustmentReadResourceV22>[],
            onSuccess?: () => void,
            onError?: () => void
        ) => {
            setErrorMessage(undefined);
            setLoading(true);
            const { items: filteredItems } = filterBasket(
                categories,
                basket.items.map(({ price: _, ...item }) => item),
                menu,
                nestedModifiersEnabled
            );
            const finalBasket: IBasketRequestResource = {
                locationId: basket.locationId,
                scenario,
                items: filteredItems,
                adjustments
            };

            if (isDefined(basket.deliveryLocation)) {
                finalBasket.deliveryLocation = basket.deliveryLocation;
            }
            if (isDefined(basket.timeSlot) && scenario !== OrderScenario.ORDER_TO_TABLE) {
                finalBasket.timeSlot = {
                    start: basket.timeSlot.start,
                    end: basket.timeSlot.end
                };
            }
            if (isDefined(deliveryAddress) && scenario === OrderScenario.DELIVERY) {
                finalBasket.delivery = {
                    dropoff: {
                        name: deliveryAddress.name,
                        note: deliveryAddress.note,
                        coordinates: deliveryAddress.coordinates,
                        address: deliveryAddress.address
                    }
                };
            }

            basketApi
                .calculateBasket(finalBasket)
                .then(newlyCalculatedBasket => {
                    setCalculatedBasket(newlyCalculatedBasket);
                    dispatch(syncBasketFromServer(newlyCalculatedBasket.items));
                    if (onSuccess) {
                        onSuccess();
                    }
                })
                .catch((err: AxiosResponse<{ code: string; httpCode: number; message: string }>) => {
                    if (err.data.code === String(OrderErrorCodes.INVALID_GIFTCARD_BALANCE)) {
                        enqueueSnackbar(err.data.message, { variant: 'error' });
                    } else {
                        const couponErrorCodes: string[] = [
                            OrderErrorCodes.COUPON_ALREADY_USED,
                            OrderErrorCodes.COUPON_NOT_VALID,
                            OrderErrorCodes.NO_ITEMS_FOR_COUPON,
                            OrderErrorCodes.COUPON_VALIDATION_FAILED,
                            OrderErrorCodes.COUPON_DUPLICATE
                        ];
                        const isCouponError = couponErrorCodes.includes(err.data.code);
                        if (isCouponError) {
                            enqueueSnackbar(err.data.message, { variant: 'error' });
                            if (onError) {
                                onError();
                            }
                        }
                        if (err.data.code === String(OrderErrorCodes.ITEMS_ARE_NOT_AVAILABLE)) {
                            setErrorMessage(err.data.message);
                        }
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [
            basket.deliveryLocation,
            basket.locationId,
            basket.timeSlot,
            basket.items,
            categories,
            deliveryAddress,
            dispatch,
            enqueueSnackbar,
            menu,
            nestedModifiersEnabled,
            scenario
        ]
    );

    const handleResetCoupons = React.useCallback(() => {
        onCouponsReset();
        onAwardsReset();
        getBasket([]);
    }, [getBasket, onAwardsReset, onCouponsReset]);

    const handleAwardsClear = React.useCallback(() => {
        onAwardsReset();
        getBasket(getSortedAdjustmentsForCheckout([], couponsForCheckout));
    }, [couponsForCheckout, getBasket, onAwardsReset]);

    const refreshBasketWithAdjustments = React.useCallback(() => {
        if (isGuest) {
            const merchantId = getLocalMerchantId();
            if (merchantId) {
                const shouldUpdateGuestSession = getShouldUpdateGuestSession(merchantId);
                if (shouldUpdateGuestSession) {
                    dispatch(createGuestSession())
                        .unwrap()
                        .then(() => {
                            resetShouldUpdateGuestSession(merchantId);
                            getBasket(
                                getSortedAdjustmentsForCheckout(awardsForCheckout, couponsForCheckout),
                                undefined,
                                handleResetCoupons
                            );
                        });
                    return;
                }
            }
        }

        getBasket(
            getSortedAdjustmentsForCheckout(awardsForCheckout, couponsForCheckout),
            undefined,
            handleResetCoupons
        );
    }, [awardsForCheckout, couponsForCheckout, dispatch, getBasket, handleResetCoupons, isGuest]);

    // initial change
    React.useEffect(() => {
        if (basket.items.length === 0) {
            setLoading(false);
            setCalculatedBasket(null);
            return;
        }
        const depsArray = [basketItemsKey, dispatch, isGuest, isLoading, menuIsReady, isAuthLoading];
        if (isChanged(depsArray, previousState.current)) {
            previousState.current = depsArray;
        } else {
            return;
        }
        if (isLoading || !menuIsReady || isAuthLoading) {
            return;
        }
        setLoading(true);

        refreshBasketWithAdjustments();
    }, [
        basket.items.length,
        basketItemsKey,
        dispatch,
        getBasket,
        isGuest,
        isLoading,
        menuIsReady,
        isAuthLoading,
        refreshBasketWithAdjustments
    ]);
    const isAnythingLoading = React.useMemo(
        () => loading || isLoading || isAuthLoading,
        [isAuthLoading, isLoading, loading]
    );
    const handleCouponAdd = React.useCallback(
        (couponCode: string) => {
            const couponAdjustments: Partial<IAdjustmentLocalResource>[] = [...couponsForCheckout];
            const selectedOn = Date.now();
            couponAdjustments.push({
                type: OrderAdjustmentType.DISCOUNT,
                couponCode,
                selectedOn
            });
            const awardsData = [...awardsForCheckout];
            const localSelectedAward = !platformAwardsEnabled && sharedData?.selectedRewards?.at(-1);
            if (localSelectedAward) {
                awardsData.push({
                    type: OrderAdjustmentType.DISCOUNT,
                    quantity: localSelectedAward.quantity,
                    awardId: localSelectedAward.awardId
                });
            }
            getBasket(
                getSortedAdjustmentsForCheckout(awardsData, couponAdjustments),
                () => {
                    onCouponAdd({ couponCode, selectedOn });
                    if (localSelectedAward) {
                        onAwardAdd(localSelectedAward);
                    }
                    onMultipleAwardsEnable();
                },
                undefined
            );
        },
        [
            awardsForCheckout,
            couponsForCheckout,
            getBasket,
            platformAwardsEnabled,
            onAwardAdd,
            onCouponAdd,
            onMultipleAwardsEnable,
            sharedData?.selectedRewards
        ]
    );
    const handleCouponRemove = React.useCallback(
        (couponCode: string) => {
            const couponAdjustments = couponsForCheckout.filter(adj => adj.couponCode !== couponCode);
            getBasket(
                getSortedAdjustmentsForCheckout(awardsForCheckout, couponAdjustments),
                () => onCouponRemove(couponCode),
                undefined
            );
        },
        [awardsForCheckout, couponsForCheckout, getBasket, onCouponRemove]
    );
    const handleAwardAdd = React.useCallback(
        (award: IUpdateOrderAdjustment) => {
            const awardAdjustments: Partial<IAdjustmentLocalResource>[] = [...awardsForCheckout];
            awardAdjustments.push({
                type: OrderAdjustmentType.DISCOUNT,
                awardId: award.awardId,
                quantity: award.quantity,
                selectedOn: Date.now()
            });
            getBasket(getSortedAdjustmentsForCheckout(awardAdjustments, couponsForCheckout), () =>
                onAwardAdd(award)
            );
        },
        [awardsForCheckout, couponsForCheckout, getBasket, onAwardAdd]
    );

    const handleAwardRemove = React.useCallback(
        (awardId: string) => {
            const awardAdjustments = awardsForCheckout.filter(adj => adj.awardId !== awardId);
            getBasket(getSortedAdjustmentsForCheckout(awardAdjustments, couponsForCheckout), () =>
                onAwardRemove(awardId)
            );
        },
        [awardsForCheckout, couponsForCheckout, getBasket, onAwardRemove]
    );

    const handleMultipleAwardsEnable = React.useCallback(
        (award: IUpdateOrderAdjustment) => {
            logUserEvent(EGAEventName.AwardsAddMoreClick);
            const awardAdjustments: Partial<IAdjustmentLocalResource>[] = [...awardsForCheckout];
            awardAdjustments.push({
                type: OrderAdjustmentType.DISCOUNT,
                awardId: award.awardId,
                quantity: award.quantity,
                selectedOn: Date.now()
            });
            getBasket(getSortedAdjustmentsForCheckout(awardAdjustments, couponsForCheckout), () =>
                onMultipleAwardsEnable(award)
            );
        },
        [awardsForCheckout, couponsForCheckout, getBasket, logUserEvent, onMultipleAwardsEnable]
    );
    return {
        calculatedBasket,
        refreshBasket: refreshBasketWithAdjustments,
        loading: isAnythingLoading,
        scenario,
        errorMessage,
        coupons: {
            value: coupons,
            onCouponAdd: handleCouponAdd,
            onCouponRemove: handleCouponRemove,
            couponAdjustments,
            showCoupons
        },
        awards: {
            value: platformAwards,
            platformAwardsEnabled,
            onClear: handleAwardsClear,
            onAwardAdd: handleAwardAdd,
            onAwardRemove: handleAwardRemove,
            onMultipleAwardsEnable: handleMultipleAwardsEnable,
            showMoreAwardsButton
        }
    };
}
